// Whiteboard engine — text layout. One layout (line breaks + widths) is
// worked out once and drawn the same way on screen, in the PNG and in the
// PDF. The three families map onto the PDF's own fonts (Helvetica, Times,
// Courier) so a PDF line breaks exactly where the screen's did.

export const LINE_HEIGHT = 1.25;
export const FAMILIES = {
  sans: 'Helvetica, Arial, sans-serif',
  serif: '"Times New Roman", Times, serif',
  mono: '"Courier New", Courier, monospace',
};
export const PDF_FONTS = { sans: 'helvetica', serif: 'times', mono: 'courier' };

export function fontCss({ size, font = 'sans', bold, italic }) {
  return `${italic ? 'italic ' : ''}${bold ? '700' : '400'} ${size}px ${FAMILIES[font] || FAMILIES.sans}`;
}

// Width of a string in a font. In a browser: the canvas's own measure; in
// Node (the suite) a fair average glyph width.
let measureCtx = null;
const cache = new Map();
function measure(text, css, size) {
  const key = `${css}|${text}`;
  const hit = cache.get(key);
  if (hit != null) return hit;
  let w;
  if (measureCtx === null && typeof document !== 'undefined') {
    try { measureCtx = document.createElement('canvas').getContext('2d'); } catch { measureCtx = false; }
  }
  if (measureCtx) { measureCtx.font = css; w = measureCtx.measureText(text).width; } else w = text.length * size * 0.55;
  if (cache.size > 20000) cache.clear();
  cache.set(key, w);
  return w;
}
export const textWidth = (text, opts) => measure(text, fontCss(opts), opts.size);

// → { lines: [{ text, width }], width, height }. maxWidth (optional) wraps
// at word breaks; a word longer than the width is broken by letters.
export function layoutText(text, opts) {
  const size = opts.size; const css = fontCss(opts);
  const max = opts.maxWidth && opts.maxWidth > size * 0.6 ? opts.maxWidth : null;
  const lines = [];
  for (const para of String(text ?? '').split('\n')) {
    if (!max) { lines.push({ text: para, width: measure(para, css, size) }); continue; }
    const words = para.split(/(\s+)/);
    let cur = '';
    for (const word of words) {
      if (!word) continue;
      const tryLine = cur + word;
      if (!cur || measure(tryLine, css, size) <= max) {
        if (!cur && measure(word, css, size) > max && !/^\s+$/.test(word)) {
          // a very long word: break it by letters
          let piece = '';
          for (const ch of word) {
            if (piece && measure(piece + ch, css, size) > max) { lines.push({ text: piece, width: measure(piece, css, size) }); piece = ''; }
            piece += ch;
          }
          cur = piece;
        } else cur = tryLine;
        continue;
      }
      lines.push({ text: cur.replace(/\s+$/, ''), width: measure(cur.replace(/\s+$/, ''), css, size) });
      cur = /^\s+$/.test(word) ? '' : word;
      if (cur && measure(cur, css, size) > max) {
        let piece = '';
        for (const ch of cur) {
          if (piece && measure(piece + ch, css, size) > max) { lines.push({ text: piece, width: measure(piece, css, size) }); piece = ''; }
          piece += ch;
        }
        cur = piece;
      }
    }
    lines.push({ text: cur.replace(/\s+$/, ''), width: measure(cur.replace(/\s+$/, ''), css, size) });
  }
  const width = lines.reduce((m, l) => Math.max(m, l.width), 0);
  return { lines, width, height: lines.length * size * LINE_HEIGHT };
}

// Where a line's baseline sits below the top of its line box.
export const baselineOffset = (size) => size * ((LINE_HEIGHT - 1) / 2 + 0.8);
